import { Box, Collapse, Divider, IconButton, Typography } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CloseIcon from "@mui/icons-material/Close";
import { useProject } from "../../context/ProjectContext";
import { useDatasources } from "../../context/DatasourceContext";
import { useDialog } from "../../context/DialogContext";
import { useLoading } from "../../context/LoadingContext";
import { useBanner } from "../../context/BannerContext";
import { DialogDescription } from "../../shared/dialog/Dialog";
import PaneTooltip from "../../shared/text/PaneTooltip";
import { clearProjectData } from "../../../excel/lifecycle";
import { clearReportWorksheets } from "../../../excel/reports/api/registry";
import { clearAllChartData } from "../../../excel/charts/api/registry";
import { getProjectReportData } from "../../../excel/workbook";
import { Project } from "../../../interfaces/Project";
import { ProjectReport } from "../../../interfaces/ProjectReport";
import { ediphiPalette } from "../../styles/colors";
import { formatLocation } from "./formatLocation";
import CardLabeledValue from "./CardLabeledValue";
import {
  PROJECT_CLEAR_ERROR,
  PROJECT_REMOVED_DATA_REMAINS,
  REMOVE_ACTION,
  REMOVE_PROJECT_TITLE,
  REMOVE_PROJECT_TOOLTIP,
  removeProjectDescription,
} from "../../constants";

const ProjectHeader: React.FC = () => {
  const { selectedProject, setSelectedProject } = useProject();
  const { showDialog, hideDialog } = useDialog();
  const { withLoading } = useLoading();
  const { showBanner } = useBanner();
  const { dataVersion } = useDatasources();
  const [expanded, setExpanded] = useState(false);
  const [projectRow, setProjectRow] = useState<Partial<ProjectReport>>();
  const lastScrollY = useRef(0);

  const projectId = selectedProject?.id;

  useEffect(() => {
    if (!projectId) return;
    let isCurrent = true;
    getProjectReportData()
      .then(([row]) => {
        if (isCurrent) setProjectRow(row?.project_id === projectId ? row : undefined);
      })
      .catch(() => undefined);
    return () => {
      isCurrent = false;
    };
  }, [projectId, dataVersion]);

  useEffect(() => {
    if (!expanded) {
      return;
    }

    const handleScroll = (e: Event) => {
      let currentScrollY = 0;

      if (e.target instanceof HTMLElement) {
        currentScrollY = e.target.scrollTop || 0;
      } else if (e.target === document || e.target === document.documentElement) {
        currentScrollY = window.scrollY || 0;
      }

      // Collapse if user is scrolling (any scroll movement)
      if (Math.abs(currentScrollY - lastScrollY.current) > 2) {
        setExpanded(false);
      }

      lastScrollY.current = currentScrollY;
    };

    // Listen to scroll events on document with capture to catch all scroll events
    // This will catch scroll events from any scrollable container
    document.addEventListener("scroll", handleScroll, { passive: true, capture: true });

    return () => {
      document.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, [expanded]);

  if (!selectedProject) {
    return null;
  }

  const shownProject: Project = projectRow
    ? {
        ...selectedProject,
        name: projectRow.project_name || selectedProject.name,
        address: projectRow.address ?? "",
        city: projectRow.city ?? "",
        state: projectRow.state ?? "",
        region: projectRow.region_name ?? "",
        client_name: projectRow.client_name ?? "",
      }
    : selectedProject;

  const handleToggleExpand = () => {
    setExpanded(!expanded);
  };

  const clearProject = async () => {
    try {
      await withLoading(async () => {
        await clearReportWorksheets();
        await clearProjectData();
        await clearAllChartData();
      });
    } catch {
      // Leave the project selected rather than half-cleared — the user can retry.
      showBanner(PROJECT_CLEAR_ERROR, "error");
      return;
    }
    // Last, for the reason ProjectSelector gives when selecting: the workbook should be consistent
    // before anything can observe the change. Also clears the estimate and marks the clear explicit.
    setSelectedProject(null);
  };

  const handleUnselectProject = () => {
    showDialog({
      title: REMOVE_PROJECT_TITLE,
      description: DialogDescription(removeProjectDescription(shownProject.name)),
      alert: {
        content: PROJECT_REMOVED_DATA_REMAINS,
      },
      submit: {
        label: REMOVE_ACTION,
        destructive: true,
        action: async () => {
          hideDialog();
          await clearProject();
        },
      },
      cancel: { action: hideDialog },
    });
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          width: "100%",
          backgroundColor: ediphiPalette.surface,
          borderBottom: "2px solid",
          borderColor: "divider",
        }}
      >
        {/* Main Header Section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            minHeight: "56px",
            "&:hover .close-button": {
              opacity: 1,
            },
          }}
        >
          {/* Left side: Project name and expand/collapse */}
          <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 1 }}>
            <IconButton onClick={handleToggleExpand} size="small" sx={{ padding: "4px" }}>
              {expanded ? (
                <ExpandLessIcon sx={{ color: ediphiPalette.mainTextColor }} />
              ) : (
                <ExpandMoreIcon sx={{ color: ediphiPalette.mainTextColor }} />
              )}
            </IconButton>
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: ediphiPalette.greyText,
                  fontSize: "0.75rem",
                  lineHeight: 1.2,
                  marginBottom: "2px",
                }}
              >
                Project
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "18px",
                  color: ediphiPalette.mainTextColor,
                }}
              >
                {shownProject.name}
              </Typography>
            </Box>
          </Box>

          {/* Right side: Close */}
          <PaneTooltip title={REMOVE_PROJECT_TOOLTIP}>
            <IconButton
              className="close-button"
              onClick={handleUnselectProject}
              size="small"
              sx={{
                opacity: 0,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              <CloseIcon sx={{ color: ediphiPalette.greyText }} />
            </IconButton>
          </PaneTooltip>
        </Box>

        {/* Expanded Details Section */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider />
          <Box
            sx={{
              p: 2,
              px: 4,
              borderLeft: selectedProject.color ? `12px solid ${selectedProject.color}` : "none",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {(shownProject.address || shownProject.city || shownProject.state) && (
                <Box>{formatLocation(shownProject)}</Box>
              )}

              {shownProject.region && (
                <CardLabeledValue
                  label="Region"
                  value={typeof shownProject.region === "string" ? shownProject.region : shownProject.region.name}
                />
              )}

              {shownProject.client_name && <CardLabeledValue label="Client" value={shownProject.client_name} />}
            </Box>
          </Box>
        </Collapse>
      </Box>
    </React.Fragment>
  );
};

export default ProjectHeader;
